// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2024-09-14 03:41:31 +0200 (Sat, 14 Sep 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 145737 $
//  Purpose   : network related sagas
// =============================================================================
// ---Third-party imports
import {take, takeEvery, takeLatest, put, select, race, call} from 'redux-saga/effects'
import {eventChannel} from 'redux-saga'
import now from 'performance-now'

// ---Framework imports
import {isNil, size, isEqual, mapItems, dict} from 'ldash'
import {Sel, Actions} from 'uix/registries'
import {delay} from 'uix/sagas'
import Api from 'uix/api'
import log from 'uix/logging'
import {canUseDOM, hashObj, timeouts} from 'uix/utils'

// ---Local imports
import {connectWebsocket} from './websocket'
import {setNetworkInfo, subscribeServerEvent, runNetCheck, setOnline} from './actions'
import {checkAuthState} from 'uix/sagas'

// ---Globals

const CHECK_INTERVAL = 10000
const FAST_CHECK_INTERVAL = 2000

function* _netcheck(lastServerTimestamp = 'INIT') {
  let interval = CHECK_INTERVAL

  let sid = yield select(Sel.sessionId)

  if (sid) {
    let ts = now()
    let params = lastServerTimestamp ? {sid, lastServerTimestamp} : {sid}

    let {data, error} = yield Api.post(
      `/auth/info`,
      params,
      {timeout: timeouts.SERVER_INFO},
      {silent: true}
    )

    let roundtrip = now() - ts

    if (error) {
      // ist error.code reliable?
      // seems to be pretty new: https://nemethgergely.com/what-is-new-in-nodejs-9/
      // ok, we get at least a NO_ERR_CODE from Api
      // this will trigger a API_OFFLINE state
      yield put(Actions.setHTTPState({error: error.code, roundtrip}))
    } else if (data) {
      // handle latency detection - the original mission
      lastServerTimestamp = data.ts

      // mission creep
      let is_expired_sid = data.expiration === null
      let {expiration, grouphash} = yield select(root => root.meta.netinfo.http)
      //~ log.x('exp: %o %o', expiration, data.expiration)
      if (is_expired_sid) {
        // old state was not expired
        if (!isNil(expiration)) {
          log.warn('session %s expired [%o]', sid.slice(0, 6), expiration)
          yield checkAuthState({})
        }
      }
      if (grouphash !== undefined && grouphash !== data.grouphash) {
        log.warn('user groups %s changed [%o]', sid.slice(0, 6), data.grouphash)
        yield checkAuthState({})
      }
      let session_info = {expiration: data.expiration, grouphash: data.grouphash}

      // update known expiration
      // this will resolve a API_OFFLINE state
      yield put(Actions.setHTTPState({error: null, roundtrip, ...session_info}))

      // handle serve side reloaded domain configs (what has been reloaded)
      if (data.reloadedDomains) {
        // XXX: we can only (easily) reinit domains if no auth change
        //~ yield put(Actions.serverDomainsReloaded(data.reloadedDomains))
      }

      // handle server side dirty domain configs (what needs to be reloaded)
      let dirtyConfigState = yield select(root => root.config.dconfigDirty)
      if (data.dirtyConfigState || dirtyConfigState) {
        if (!isEqual(dirtyConfigState, data.dirtyConfigState)) {
          yield put(Actions.setDirtyConfigState({dirty: data.dirtyConfigState}))
        }
      }

      // track running builds
      let runningBuilds = yield select(root => root.debug.runningBuilds)
      if (size(runningBuilds) > 0 || size(data.runningBuilds) > 0) {
        yield put(Actions.setDebugData({runningBuilds: data.runningBuilds}))
        // increase notification resolution
        interval = FAST_CHECK_INTERVAL
      }
    }
  }
  return {interval, lastServerTimestamp}
}

function* loopUnauthorizedView() {
  /* if in VIEW_UNAUTHORIZED state, check auth situation periodically
   */
  if (process.env.UIX_ENV === 'express') return
  // wait for first view activation (maybe use SET_APP_STATE?)
  yield take('ACTIVATE_VIEW')

  while (true) {
    let appState = yield select(root => root.meta.state)
    let window_visible = yield select(root => root.meta.window.visible)
    if (appState == 'VIEW_UNAUTHORIZED') {
      if (window_visible) {
        yield checkAuthState({})
      } else {
        log.info('skip checkAuthState, window invisible')
      }
    }
    yield delay(2000)
  }
}

function* loopNetCheck() {
  if (process.env.UIX_ENV === 'express') return

  let lastServerTimestamp = 'INIT'
  // wait for first view activation (maybe use SET_APP_STATE?)
  yield take('ACTIVATE_VIEW')

  try {
    while (true) {
      let res = yield _netcheck(lastServerTimestamp)
      let interval = res.interval
      lastServerTimestamp = res.lastServerTimestamp

      yield race([
        delay(interval - 600),
        // faster wakeup
        take('SAVE_DOMAIN_CONFIG'),
        take('SAVE_DOMAIN_CONFIG_VALUES'),
        take('REBUILD_DOMAIN_CONFIG_DONE'),
      ])

      // hack hack: for won race of wakeup events
      yield delay(600)
    }
  } finally {
    log.debug('shutdown netcheck')
  }
}

const getNetinfo = () => {
  const {connection} = navigator
  let {downlink, rtt, effectiveType, saveData} = connection
  return {downlink, rtt, effectiveType, saveData}
}
function createConnectionChannel() {
  const {connection} = navigator
  return eventChannel(emit => {
    let prevStatus = null
    let onChange = () => {
      let netinfo = getNetinfo()
      if (hashObj(netinfo) !== prevStatus) {
        emit(netinfo)
        prevStatus = hashObj(netinfo)
      }
    }
    connection.addEventListener('change', onChange)
    return () => connection.removeEventListener('change', onChange)
  })
}

function* handleConnectionChange(netinfo) {
  yield put(setNetworkInfo(netinfo))
}

function* monitorNetworkConnection() {
  if (!(canUseDOM && navigator.connection)) return
  yield put(setNetworkInfo(getNetinfo()))

  const connectionChannel = yield createConnectionChannel()
  // XXX: behaves strangely
  yield takeLatest(connectionChannel, handleConnectionChange)
}

function createNetworkStatusChannel() {
  return eventChannel(emit => {
    // Define a handler that emits the current network status
    const networkStatusHandler = () => emit(navigator.onLine)

    // Listen for online and offline events
    window.addEventListener('online', networkStatusHandler)
    window.addEventListener('offline', networkStatusHandler)

    // The return function is the cleanup function
    // that will be invoked when the channel is closed
    return () => {
      window.removeEventListener('online', networkStatusHandler)
      window.removeEventListener('offline', networkStatusHandler)
    }
  })
}

function* watchNetworkStatus() {
  if (!canUseDOM) return
  const channel = yield call(createNetworkStatusChannel)
  yield put(setOnline(navigator.onLine))
  while (true) {
    let online = yield take(channel)
    yield put(setOnline(online))
  }
}

function* loopSubscribeServerEvent() {
  /* we are watching root.data.serverEvents.subscribed for changes
   */
  if (process.env.UIX_ENV === 'express') return
  let known_subscribed = {}
  while (true) {
    yield delay(500)
    let should_subscribed = yield select(root => root.data.serverEvents.subscribed)
    // first filter for new activations, then for new deactivations
    // maybe change api to use one-shot
    for (let active of [true, false]) {
      let eventName = mapItems(
        should_subscribed,
        k => k,
        (k, v) => v == active && v !== known_subscribed[k]
      )

      if (eventName.length > 0) {
        let {data, error} = yield Api.get('/api/events/subscribeEventOverWebsocket', {
          eventName,
          active,
        })
        if (error) {
          log.error('error during server event subscription: %o [activate: %o]', eventName, active)
        } else if (size(data.auth_failed)) {
          log.warn('event registration, missing auth: %o', data.auth_failed)
        }

        known_subscribed = {...known_subscribed, ...dict(eventName.map(e => [e, active]))}
      }
    }
  }
}

export const sagas = {
  connectWebsocket: takeEvery('SET_APP_STATE', function*({payload}) {
    let prevState = yield select(root => root.meta.prevState)
    // wee need to trigger on changes of instance-cfg and user
    if (payload.state == 'APP_RUNNING' && ['INIT_APP', 'VIEW_UNAUTHORIZED'].includes(prevState)) {
      // is authenticated
      yield connectWebsocket()
    }
  }),
  loopSubscribeServerEvent: loopSubscribeServerEvent(),
  monitorNetworkConnection: monitorNetworkConnection(),
  watchNetworkStatus: watchNetworkStatus(),
  loopUnauthorizedView: loopUnauthorizedView(),
  loopNetCheck: loopNetCheck(),
  runNetCheck: takeEvery(runNetCheck, function*() {
    yield _netcheck()
  }),
}
